import { Phone, QrCode, ShieldCheck, Heart, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function FlyerPage() {
  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="bg-white">
      {/* Print Button - Hidden when printing */}
      <div className="print:hidden fixed top-4 right-4 z-50 flex gap-2 no-print">
        <Button onClick={handlePrint} className="bg-teal-600 hover:bg-teal-700">
          Print / Save PDF
        </Button>
        <Button variant="outline" onClick={() => window.history.back()}>
          Back
        </Button>
      </div>

      {/* Flyer Content - Clean design matching landing page */}
      <div className="flyer-container w-[8.5in] mx-auto bg-white overflow-hidden flex flex-col print-container">
        
        {/* Header - Clean style like landing page with image */}
        <div className="px-8 pt-4 pb-3 border-b border-slate-200">
          <div className="flex items-center gap-6">
            {/* Left side - Text content */}
            <div className="flex-1 text-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_bracelet-alert/artifacts/suao9dvz_SFA%20Logo%20-%20long.png" 
                alt="Stress-Free Aging" 
                className="w-56 h-auto mx-auto"
              />
              <h1 className="text-3xl font-bold text-teal-600 mt-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Stress-Free Aging SOS
              </h1>
              <div className="inline-flex items-center gap-2 bg-teal-50 text-teal-700 px-3 py-1 rounded-full text-xs font-medium mt-1">
                <ShieldCheck className="h-3 w-3" />
                HIPAA-Conscious Emergency ID
              </div>
              <h2 className="text-lg font-semibold text-slate-900 mt-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Peace of Mind for <span className="text-teal-600">Families</span> & <span className="text-teal-600">Caregivers</span>
              </h2>
            </div>
            {/* Right side - Gentleman image */}
            <div className="flex-shrink-0">
              <img 
                src="https://customer-assets.emergentagent.com/job_b27e985d-d66e-4f89-aa44-c3370ef952c0/artifacts/q5dx831y_imagejpeg_1%284%29.jpg" 
                alt="Senior wearing QR bracelet" 
                className="w-44 h-44 object-cover rounded-xl shadow-lg"
              />
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 px-8 py-3">
          
          {/* Bracelet Image + Description Row */}
          <div className="flex gap-5 mb-3">
            {/* Bracelet Image */}
            <div className="flex-shrink-0">
              <img 
                src="https://customer-assets.emergentagent.com/job_b27e985d-d66e-4f89-aa44-c3370ef952c0/artifacts/4tfa7v3l_imagejpeg_2%283%29.jpg" 
                alt="QR Code Medical Bracelets - Black, Pink, Red" 
                className="w-40 h-auto rounded-xl shadow-lg"
              />
            </div>
            
            {/* Description */}
            <div className="flex-1">
              <p className="text-sm text-slate-700 mb-2">
                Our <strong className="text-teal-600">QR-enabled bracelets</strong> connect your loved ones to emergency help instantly. One scan provides critical information to first responders when every second counts.
              </p>
              <div className="grid grid-cols-2 gap-1.5">
                <div className="flex items-center gap-2 bg-slate-50 px-2 py-1.5 rounded-lg border border-slate-100">
                  <QrCode className="h-3 w-3 text-teal-600" />
                  <span className="text-xs"><strong>One scan</strong> reveals info</span>
                </div>
                <div className="flex items-center gap-2 bg-slate-50 px-2 py-1.5 rounded-lg border border-slate-100">
                  <Phone className="h-3 w-3 text-teal-600" />
                  <span className="text-xs"><strong>One tap</strong> to call</span>
                </div>
                <div className="flex items-center gap-2 bg-slate-50 px-2 py-1.5 rounded-lg border border-slate-100">
                  <ShieldCheck className="h-3 w-3 text-teal-600" />
                  <span className="text-xs"><strong>HIPAA-conscious</strong></span>
                </div>
                <div className="flex items-center gap-2 bg-pink-50 px-2 py-1.5 rounded-lg border border-pink-100">
                  <AlertTriangle className="h-3 w-3 text-pink-600" />
                  <span className="text-xs"><strong>Dementia alert</strong></span>
                </div>
              </div>
            </div>
          </div>

          {/* How It Works Section */}
          <div className="bg-slate-50 rounded-xl p-3 mb-3 border border-slate-100">
            <h2 className="text-base font-bold text-center text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              How It Works
            </h2>
            <div className="flex justify-center gap-6">
              <div className="text-center flex-1">
                <div className="w-8 h-8 bg-teal-600 text-white rounded-full flex items-center justify-center font-bold text-sm mx-auto mb-1">1</div>
                <p className="font-semibold text-xs text-slate-900">Get Your Bracelet</p>
                <p className="text-xs text-slate-500">Order from us today</p>
              </div>
              <div className="text-center flex-1">
                <div className="w-8 h-8 bg-teal-600 text-white rounded-full flex items-center justify-center font-bold text-sm mx-auto mb-1">2</div>
                <p className="font-semibold text-xs text-slate-900">Register Online</p>
                <p className="text-xs text-slate-500">Scan QR & add info</p>
              </div>
              <div className="text-center flex-1">
                <div className="w-8 h-8 bg-teal-600 text-white rounded-full flex items-center justify-center font-bold text-sm mx-auto mb-1">3</div>
                <p className="font-semibold text-xs text-slate-900">Stay Protected</p>
                <p className="text-xs text-slate-500">Anyone can scan to help</p>
              </div>
            </div>
          </div>

          {/* Order Section - Clean teal accent */}
          <div className="bg-teal-600 rounded-xl p-3 text-white">
            <h2 className="text-lg font-bold text-center mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Order Your Bracelet Today!
            </h2>
            <div className="flex justify-center items-center gap-5">
              {/* Website QR */}
              <div className="text-center">
                <div className="bg-white p-1 rounded-lg">
                  <img 
                    src="https://api.qrserver.com/v1/create-qr-code/?size=100x100&data=https://sos.stressfreeaging.com/order" 
                    alt="Order QR"
                    className="w-14 h-14"
                  />
                </div>
                <p className="text-xs font-medium mt-0.5 text-teal-100">Order Here</p>
              </div>
              
              {/* Contact Info - Center */}
              <div className="text-center">
                <p className="text-lg font-bold">stressfreeaging.com</p>
                <p className="text-xl font-bold">(586) 306-8449</p>
                <p className="text-sm mt-0.5 bg-yellow-400 text-yellow-900 font-bold px-3 py-0.5 rounded-full inline-block">
                  Only $49!
                </p>
              </div>

              {/* Zelle QR */}
              <div className="text-center">
                <div className="bg-white p-1 rounded-lg">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_bracelet-alert/artifacts/d0mu1r15_Screenshot_20260201_165944_Word.jpg" 
                    alt="Pay with Zelle"
                    className="w-14 h-14 object-contain"
                  />
                </div>
                <p className="text-xs font-medium mt-0.5 text-teal-100">Pay with Zelle</p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer - Simple and clean */}
        <div className="bg-slate-800 px-6 py-2 text-center">
          <p className="text-white font-medium text-sm">
            <span className="text-teal-400">Perfect For:</span> Seniors with Dementia | Alzheimer's | Wandering Risk | Memory Care
          </p>
          <p className="text-slate-400 text-xs">stressfreeaging@gmail.com</p>
        </div>
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          @page {
            size: letter;
            margin: 0.3in;
          }
          html, body {
            width: 8.5in;
            height: 11in;
            margin: 0;
            padding: 0;
          }
          body {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .no-print, .print\\:hidden {
            display: none !important;
          }
          .print-container {
            width: 100% !important;
            max-width: 8in !important;
            margin: 0 auto !important;
            page-break-after: avoid;
            page-break-inside: avoid;
          }
        }
        
        @media screen {
          .flyer-container {
            box-shadow: 0 4px 20px rgba(0,0,0,0.15);
            margin: 20px auto;
            max-height: 10.5in;
          }
        }
      `}</style>
    </div>
  );
}
